/* MAA Kit Builder — core engine.
   Platform-agnostic. All product, ladder and rule data lives in per-platform JSON
   assets (platform-<id>.json) which are loaded on demand. Nothing about a specific
   bike, price or SKU belongs in this file.

   Speaker positions are named with the store's own vocabulary (the values used in
   the custom.location_tag metafield): "Fairing", "Lower Fairing", "Saddlebags",
   "Tour Pack". A model declares which positions it physically has; the rider picks
   a subset; everything else is derived. A model without Saddlebags — a trike —
   can never be offered a saddlebag speaker, because the option is generated from
   the model rather than from a shared list.

   Public API:
     MAAKB.loadPlatform(id, url)  -> Promise<platform>
     MAAKB.platform(id)           -> platform | null
     MAAKB.newState(platform)     -> state seeded with option defaults
     MAAKB.positionsFor(p, s)     -> positions this model offers
     MAAKB.visibleGroups(p,s)     -> option groups whose `when` is satisfied
     MAAKB.resolve(p, s)          -> {lines,total,targets,wiring,incomplete,preorder,...}
     MAAKB.levelTotal(p,s,levelId)-> number | null
     MAAKB.diff(a,b)              -> {added,removed,delta,kept}
     MAAKB.sizeFor(p,s,pos)       -> speaker size this model gets at that position
*/
(function (root) {
  "use strict";

  var CACHE = {};
  var FRONT = "Fairing";   // the position that always gets its own channel pair

  /* BOM order. The bands below are the column order of the BOM Matrix sheet —
     amplifier, speakers front to back, then fitment, then the harnessing, then
     whatever connects the audio source. Riders read a kit that way and the
     workshop builds it that way, so the builder's parts list and the cart both
     follow it rather than the order the rules happen to fire in.

     Gaps are deliberate: `base` and the option groups expand into their band by
     declaration index, so a platform file can grow a part without renumbering
     anything. */
  var ORD = { amp: 10, speaker: 20, base: 40, option: 70, harness: 90, other: 95 };

  /* ---------------------------------------------------------------- loading */
  // One request per platform, ever. Byte count and timing are recorded on the
  // platform as _meta so callers never need a second fetch to report them.
  function loadPlatform(id, url) {
    if (CACHE[id]) return Promise.resolve(CACHE[id]);
    var t0 = (typeof performance === "object" && performance.now) ? performance.now() : 0;
    return fetch(url).then(function (r) {
      if (!r.ok) throw new Error("platform " + id + ": HTTP " + r.status);
      return r.text();
    }).then(function (txt) {
      var p = register(JSON.parse(txt));
      p._meta = {
        bytes: txt.length,
        ms: ((typeof performance === "object" && performance.now) ? performance.now() : 0) - t0
      };
      return p;
    });
  }
  function register(p) {
    if (p.schema_version !== 2) throw new Error("unsupported schema_version " + p.schema_version);
    indexFamilies(p);
    CACHE[p.id] = p;
    return p;
  }

  /* Which of THIS PLATFORM'S parts the live maps actually cover.

     `pricedBaked` on a resolved build only reports the ten or so parts that
     build touches, so a systematically truncated map hides in plain sight: on
     28 Aug a `limit: 50 / offset: 50` pair silently produced nothing past the
     first fifty, and thirteen parts were served from baked data while the
     feature looked like it was working. One tested part happened to be inside
     the fifty, so it was reported as confirmed.

     This checks every part the platform can reference, which is the question
     that would have caught it. Cheap, so it runs on every resolve. */
  function coverage(p) {
    var total = 0, price = 0, stock = 0, missingPrice = [], missingStock = [];
    for (var sku in p.parts) {
      var pt = p.parts[sku];
      total++;
      if (livePriceFor(pt) !== null) price++; else missingPrice.push(sku);
      if (liveStockFor(pt) !== null) stock++; else missingStock.push(sku);
    }
    return {
      parts: total,
      price: price, stock: stock,
      priceComplete: LIVE ? price === total : null,
      stockComplete: STOCK ? stock === total : null,
      missingPrice: missingPrice, missingStock: missingStock
    };
  }
  function platform(id) { return CACHE[id] || null; }

  // fam/ohm -> sku. Built once per platform so impedance selection is a lookup
  // rather than a scan, and so a missing 4Ω twin fails loudly at resolve time.
  function indexFamilies(p) {
    var ix = {};
    for (var sku in p.parts) {
      var pt = p.parts[sku];
      if (!pt.fam || !pt.ohm) continue;
      (ix[pt.fam] = ix[pt.fam] || {})[pt.ohm] = sku;
    }
    p._fam = ix;
  }
  function skuFor(p, fam, ohm) {
    var f = p._fam[fam];
    return (f && f[ohm]) || null;
  }

  /* A level may name one amplifier or several.

     `amp` (a string) stays the shape for every existing level and is what the
     five-level ladder uses. `amps` (an array) is for builds that split the
     speakers across two amplifiers. Everything downstream reads this helper, so
     nothing has to know which form the level was authored in. */
  function ampsOf(L) {
    if (L.amps && L.amps.length) return L.amps;
    return L.amp ? [L.amp] : [];
  }
  function primaryAmp(L) { return ampsOf(L)[0] || null; }

  /* The amplifier record that ACTUALLY drives this build.

     Normally that is the Cicada part the level names. On a speaker-only level
     there is no Cicada amplifier - the bike keeps its factory one - and every
     safety-relevant figure (channel count, minimum impedance, watts per
     channel) has to come from the model's `oemAmp` record instead.

     Before this existed, `p.parts[primaryAmp(L)] || {}` returned `{}` and the
     `|| 4` / `|| 2` defaults downstream produced "four channels at 2 ohms".
     That happens to describe the Rockford, so the engine defaulted into being
     right and reported `unsafe: false` on a basis it had never established. A
     build whose amplifier is FIXED is the last place a guessed rating belongs.

     Falls through to `{}` when there is neither, which preserves the existing
     behaviour of level 7 - authored with no `amp`, on a bike with no `oemAmp` -
     exactly. */
  function ampRecord(p, L, M) {
    return p.parts[primaryAmp(L)] || (M && M.oemAmp) || {};
  }

  /* DSP is either inside the amplifier or a box of its own. Both are "this
     system has DSP" as far as the badge and the copy are concerned. */
  function hasDsp(L) { return !!(L.dsp || L.dspPart); }

  /* ------------------------------------------------------------ LIVE PRICES

     `parts[sku].p` is BAKED — written by _gen_parts.py at generation time. The
     cart is priced by Shopify from the variant id, so the number displayed and
     the number charged come from two different sources. Baked-only meant a
     price change in Shopify silently split them, and the first sign would have
     been a customer being charged more than they were quoted.

     So the host section renders a variantId -> price-in-cents map from the
     `maa-builder-parts` collection, and it is installed here. Baked `p` stays
     as the fallback: it keeps the offline verification tooling working, and a
     missing live price is no worse than the old behaviour.

     Nothing here guesses. If the map has no entry for a variant, the build
     says so — see `priceSource` and `pricedBaked` on the resolve output —
     rather than quietly quoting a figure of unknown age. */
  var LIVE = null;

  function setLivePrices(map) {
    LIVE = (map && typeof map === "object") ? map : null;
    return LIVE ? Object.keys(LIVE).length : 0;
  }

  // Cents from the live map, or null when there is no usable entry.
  function livePriceFor(pt) {
    if (!LIVE || !pt || pt.vid == null) return null;
    var c = LIVE[pt.vid];
    return typeof c === "number" && isFinite(c) ? c : null;
  }

  /* The price to charge for one unit. Live if we have it, baked if we do not.
     Divided by 100 because Shopify's Liquid renders money in cents while the
     platform files carry dollars - a units mismatch here would be off by a
     factor of a hundred and look like a catastrophe rather than a bug. */
  function priceOf(pt) {
    var c = livePriceFor(pt);
    if (c !== null) return c / 100;
    return (pt && typeof pt.p === "number") ? pt.p : 0;
  }

  /* ------------------------------------------------------- LIVE STOCK

     `q` and `policy` were baked alongside `p`, with the same consequence: 13
     policy changes made on 28 Aug never reached the builder, so it would have
     refused sales Shopify was happy to take. Same fix, same shape, but a
     SEPARATE map and a separate block - the price path had just been confirmed
     working against production and there was no reason to disturb it. If stock
     fails, prices still work.

     Each entry is [qty, continueFlag, availableFlag, trackedFlag].

     `available` is Shopify's own answer to "can this be bought right now",
     which already folds stock and policy together, so it decides whether a
     line BLOCKS. Quantity only decides the wording after that. `tracked`
     matters because an untracked variant reports quantity 0 while being
     permanently in stock - reading that as a pre-order would put a note on
     every build carrying one. */
  var STOCK = null;

  function setLiveStock(map) {
    STOCK = (map && typeof map === "object") ? map : null;
    return STOCK ? Object.keys(STOCK).length : 0;
  }

  function liveStockFor(pt) {
    if (!STOCK || !pt || pt.vid == null) return null;
    var r = STOCK[pt.vid];
    if (!r || typeof r.length !== "number" || r.length < 4) return null;
    for (var i = 0; i < 4; i++) if (typeof r[i] !== "number" || !isFinite(r[i])) return null;
    return { q: r[0], cont: !!r[1], avail: !!r[2], tracked: !!r[3] };
  }

  /* "ok" | "preorder" | "confirm", from live data where we have it.

     The baked branch is the behaviour that shipped on 27 Aug and is kept
     verbatim, so a missing map changes nothing rather than changing something
     subtly. */
  function stockStateOf(pt) {
    var st = liveStockFor(pt);
    if (st) {
      if (!st.tracked) return "ok";
      if (!st.avail) return "confirm";
      return st.q <= 0 ? "preorder" : "ok";
    }
    if (pt && pt.q != null && pt.q <= 0) return pt.policy === "DENY" ? "confirm" : "preorder";
    return "ok";
  }

  /* Watts per channel AT THE LOAD THE CHANNEL ACTUALLY SEES.

     `wpc` is the 2Ω figure, which is the headline number on every Cicada
     amplifier. Publishing it against a 4Ω channel would overstate the target by
     around 55% — the FLX1400.4 is 350W x 4 at 2Ω and 225W x 4 at 4Ω. So an amp
     part may carry `wpcAt` keyed by ohms; where it does, the load decides.

     No `wpcAt` entry means no published figure for that load, and the honest
     answer is the headline rating rather than an interpolation. Amplifier power
     against impedance is not linear and guessing it would produce a number an
     installer might set gains against. */
  function wpcFor(amp, ohm) {
    if (!amp) return null;
    if (amp.wpcAt && amp.wpcAt[ohm] != null) return amp.wpcAt[ohm];
    return amp.wpc || null;
  }

  /* ------------------------------------------------------------ derivations */
  function levelOf(p, s) {
    for (var i = 0; i < p.levels.length; i++) if (p.levels[i].id === s.level) return p.levels[i];
    return null;
  }
  function modelOf(p, s) { return (s.model && p.models[s.model]) || null; }

  // Positions this model physically has, in platform-declared display order.
  function positionsFor(p, s) {
    var M = modelOf(p, s);
    if (!M) return [];
    return (p.positionOrder || []).filter(function (k) {
      return (M.positions || []).indexOf(k) >= 0;
    });
  }
  // Positions the rider has actually selected. The front position is implicit —
  // there is no system without it — so it is always present.
  function chosen(p, s) {
    var avail = positionsFor(p, s);
    var want = s.positions || [];
    var out = avail.filter(function (k) {
      return k === FRONT || want.indexOf(k) >= 0;
    });
    return out;
  }
  function groupById(p, id) {
    var gs = p.optionGroups || [];
    for (var i = 0; i < gs.length; i++) if (gs[i].id === id) return gs[i];
    return null;
  }
  function choiceById(g, id) {
    for (var i = 0; i < g.choices.length; i++) if (g.choices[i].id === id) return g.choices[i];
    return null;
  }

  /* ------------------------------------------------------------------ wiring
     Four-channel amplifiers rated at 2Ω. One speaker pair per channel pair while
     there is room; beyond that, pairs double up and must be 4Ω so the parallel
     load lands back on 2Ω. The impedance is therefore a property of the
     configuration, not of the level — and it is computed, never authored. */
  /* Wiring from an authored channel map.

     Each entry is one channel PAIR on one amplifier, and the positions it
     drives. Two positions on a pair means their speakers sit in parallel, so
     the presented load is the speaker impedance halved; one position means the
     amplifier sees the speaker impedance directly.

     Impedance is read from the parts the level names, never assumed — a level
     that maps 4Ω drivers one-per-pair presents 4Ω, and saying "2Ω because the
     amplifier is rated to 2Ω" would be a fiction that the target maths would
     then publish as a voltage. */
  function mapWiring(p, L, pos) {
    var ohm = {}, load = {}, ampOf = {}, pairOf = {}, mates = {};
    var chTotal = 0, unsafe = false, minRated = null;

    ampsOf(L).forEach(function (a) {
      var pt = p.parts[a] || {};
      chTotal += pt.ch || 4;
      var mo = pt.minOhm || 2;
      minRated = (minRated === null || mo > minRated) ? mo : minRated;
    });

    L.channels.forEach(function (c) {
      var ampSku = ampsOf(L)[c.amp || 0];
      var ampPt = p.parts[ampSku] || {};
      var on = (c.positions || []).filter(function (k) { return pos.indexOf(k) >= 0; });
      if (!on.length) return;
      on.forEach(function (k) {
        var sku = (L.spk || {})[k];
        var spk = sku && p.parts[sku];
        var o = spk && spk.ohm ? spk.ohm : (ampPt.minOhm || 2);
        ohm[k] = o;
        load[k] = o / on.length;          // parallel
        ampOf[k] = ampSku;
        pairOf[k] = c.pair || 1;
        mates[k] = on.slice();
        if (load[k] < (ampPt.minOhm || 2) - 1e-9) unsafe = true;
      });
    });

    // A position the map forgot is a fault in the level, not a rider problem.
    var unmapped = pos.filter(function (k) { return !(k in ohm); });

    var min = null;
    for (var k in load) min = (min === null || load[k] < min) ? load[k] : min;

    /* Capacity means "does the map fit on the amplifiers it names", NOT "is
       there a channel pair per position".

       The derived rule counts one pair per position, because that is what it
       builds. A map exists precisely to put two positions on one pair — so
       reusing that test here reported a four-position build on a four-channel
       amplifier as beyond capacity, which is the exact configuration the level
       was written to express. What actually has to hold is that no amplifier is
       asked for more channel pairs than it has, and that is counted from the
       map. Every position being mapped is checked separately above. */
    var pairsUsed = {}, over = false;
    L.channels.forEach(function (c) {
      var i = c.amp || 0;
      (pairsUsed[i] = pairsUsed[i] || {})[c.pair || 1] = true;
    });
    ampsOf(L).forEach(function (a, i) {
      var have = ((p.parts[a] || {}).ch || 4) / 2;
      if (Object.keys(pairsUsed[i] || {}).length > have) over = true;
    });
    var need = 0;
    for (var ai in pairsUsed) need += Object.keys(pairsUsed[ai]).length;

    return {
      channels: chTotal, pairsAvailable: chTotal / 2, pairsNeeded: need,
      sharing: Object.keys(mates).some(function (k) { return mates[k].length > 1; }),
      perChannelPair: 1,
      ohm: ohm, presentedLoad: load, minLoad: min,
      minRated: minRated || 2,
      ampOf: ampOf, pairOf: pairOf, mates: mates, mapped: true,
      overCapacity: over,
      unmapped: unmapped,
      unsafe: unsafe
    };
  }

  function planWiring(p, L, pos, M) {
    /* An explicit channel map overrides the derivation entirely.

       The rule below is a good default and produces the right answer for the
       five-level ladder, but it groups by position order — front keeps a pair,
       everything else shares what is left. Some builds group by ROLE instead:
       full-range on one channel pair, dedicated mid-bass on the other, which
       puts the fairing with the Tour-Pak and the lowers with the saddlebags.
       No ordering rule produces that, so those levels state it outright and the
       engine stops guessing. */
    if (L.channels && L.channels.length) return mapWiring(p, L, pos);

    var amp = ampRecord(p, L, M);
    var ch = amp.ch || 4;
    var pairs = ch / 2;                    // channel pairs available
    var need = pos.length;                 // speaker pairs required
    var perPair = Math.ceil(need / pairs); // how many speaker pairs share a channel pair

    // The front keeps its own channel pair; everything else shares what is left.
    var rear = pos.filter(function (k) { return k !== FRONT; });
    var rearPairs = Math.max(pairs - 1, 0);
    var rearPerPair = rearPairs ? Math.ceil(rear.length / rearPairs) : 0;

    var ohm = {};
    ohm[FRONT] = amp.minOhm || 2;
    rear.forEach(function (k) { ohm[k] = (amp.minOhm || 2) * (rearPerPair || 1); });

    // Presented load per channel pair, after paralleling. Anything under the
    // amplifier's rating is refused rather than warned about.
    var load = {};
    load[FRONT] = ohm[FRONT];
    rear.forEach(function (k) { load[k] = ohm[k] / (rearPerPair || 1); });

    var min = null;
    for (var k in load) min = (min === null || load[k] < min) ? load[k] : min;

    return {
      channels: ch, pairsAvailable: pairs, pairsNeeded: need,
      sharing: rearPerPair > 1, perChannelPair: perPair,
      ohm: ohm, presentedLoad: load, minLoad: min,
      minRated: amp.minOhm || 2,
      overCapacity: need > pairs * 2,
      unsafe: min !== null && min < (amp.minOhm || 2) - 1e-9
    };
  }

  // Facts a `when` clause may test. Position membership is exposed as pos_<key>
  // booleans so rules stay declarative and the matcher needs no special cases.
  function facts(p, s) {
    var L = levelOf(p, s);
    var pos = chosen(p, s);
    var src = null, g = groupById(p, "source");
    if (g) src = choiceById(g, s[g.state]);
    var hasHU = !!(src && (src.hu || src.own));
    var huKind = src ? (src.own ? (s.ownHu || null) : src.hu || null) : null;
    /* Which harness, if any. The rules live in the platform file and are tested
       against plain state, so the engine needs no notion of head units or
       Maestro modules. "An MAA harness is fitted" is simply "a rule matched" —
       which is what the base rules key on to drop the Cicada looms. */
    var harnessSku = null, harnessLabel = null, hrules = (p.harness && p.harness.rules) || [];
    for (var hi = 0; hi < hrules.length; hi++) {
      if (matches(hrules[hi].when, {}, s)) {
        harnessSku = hrules[hi].sku; harnessLabel = hrules[hi].label || null; break;
      }
    }
    var maaHarness = !!harnessSku;
    // True only where MAA is supplying the head unit, so the loom question can
    // be asked there without duplicating the "which head unit" question.
    var maaSupplied = !!(src && src.hu && !src.own);

    var f = {
      positions: pos,
      speakers: pos.length * 2,
      /* `dsp` SELECTS THE A2B INTERFACE. It is deliberately not `hasDsp(L)`.

         `true`  -> ZEN-H PRO (8-channel A2B **with** DSP) + the direct harness
         `false` -> 24HDZENHCAKIT (plain A2B, hands off line level)

         It was originally documented as "the amplifier has integrated DSP",
         because until 28 Aug those two things coincided on every level. **Level
         6 is now a counterexample**: B9 dropped its amplifier from the
         FLX1400.4PRO to the plain FLX1400.4 precisely so the ZEN-H PRO would be
         the system's ONLY processor rather than its second, and the level still
         needs `dsp: true` to order it. So read this flag as a statement about
         WHICH INTERFACE the level takes, never as a statement about the
         amplifier. Do not "correct" level 6 to `dsp: false` — that swaps the
         processor out of the build entirely.

         A level carrying an EXTERNAL processor still needs the plain interface,
         because the processor is downstream of it — level 7 is the example, with
         `dspPart: DSP412PRO` and `dsp: false`. Widening this fact to "the system
         has DSP somewhere" would silently order the wrong A2B hardware.

         hasDsp() answers the customer-facing question ("does this system have
         DSP?") and is used for the badge. Two different questions, two
         different answers, and they must not be merged. */
      dsp: L ? !!L.dsp : false,
      levelPkg: L ? !!L.pkg : false,
      hasHeadUnit: hasHU,
      huKind: huKind,
      maaHarness: maaHarness,
      maaSupplied: maaSupplied,
      harnessSku: harnessSku,
      harnessLabel: harnessLabel,
      lids: s.lids,
      source: s.source,
      ownHu: s.ownHu
    };
    (p.positionOrder || []).forEach(function (k) {
      f["pos_" + k.replace(/\s+/g, "_")] = pos.indexOf(k) >= 0;
    });
    // Model traits — things true of the bike rather than of the build, such as
    // whether it could have left the factory with amplified audio. Exposed as
    // trait_<name> so a rule can test one without the engine knowing any of them.
    var M = modelOf(p, s);
    ((M && M.traits) || []).forEach(function (t) { f["trait_" + t] = true; });

    /* The factory amplifier, as TWO SEPARATE facts. They are deliberately not
       one field. `has_oemAmp` answers "may this bike keep its factory
       amplifier?" and gates whether the speaker-only level is offered at all;
       `oemRear` answers "are that amplifier's rear channels usable?" and gates
       the Saddlebags position on it. The Road Glide RR is exactly the case that
       needs both - its amplifier qualifies on an A1 parts catalogue, while its
       rear channels are unproven - and one field carrying two meanings is the
       fault already recorded as B10. */
    f.has_oemAmp = !!(M && M.oemAmp);

    /* Whether the FACTORY FRONT STAGE can honestly be improved on by the
       speakers this pathway fits. Separate from `has_oemAmp` because they
       answer different questions and one bike answers them differently: the
       CVO Road Glide RR's amplifier qualifies, but its factory fairing
       speakers are Stage III - 3-way, 250 W RMS each - against which the
       CX65.2 (2-way coaxial, 100 W RMS) is not an upgrade. Eligibility to
       KEEP the amplifier is not eligibility to REPLACE the speakers.

       Fails CLOSED: a model with no `oemFront` record is not offered the
       speaker-only level. A gate about whether we can honestly call something
       an upgrade must not default to yes. */
    f.oemFrontUpgradeable = !!(M && M.oemFront && M.oemFront.upgradeable);
    f.oemRear = (M && M.oemAmp && M.oemAmp.rearOut) || null;

    /* Two amplifiers is a physical fact about the build, not a property of one
       named level. Derived from the level's own amp list, so a future dual-amp
       level gets the second mounting rack, the saddlebag harness and the heavy
       power kit automatically instead of silently shipping one rack and an
       undersized kit - which is the same silent-skip that shipped the
       FLX1400.4PRO with no power kit at all. */
    f.dualAmp = ampsOf(L || {}).length > 1;

    /* True only on a level that keeps the factory amplifier. Defaults false, so
       every level authored before this - including level 7, which has no `amp`
       either - keeps precisely the base list it has today. */
    f.ampRetained = !!(L && L.ampRetained);

    return f;
  }
  function matches(when, f, s) {
    if (!when) return true;
    for (var k in when) {
      var want = when[k];
      var got = (k in f) ? f[k] : s[k];
      if (want === true || want === false) { if (!!got !== want) return false; }
      else if (got !== want) return false;
    }
    return true;
  }

  function visibleGroups(p, s) {
    var f = facts(p, s);
    return (p.optionGroups || []).filter(function (g) { return matches(g.when, f, s); });
  }

  function newState(p) {
    var s = { model: null, positions: null, level: null, note: "" };
    (p.optionGroups || []).forEach(function (g) { s[g.state] = g.default; });
    return s;
  }

  /* -------------------------------------------------------------- resolution */
  function resolve(p, s) {
    if (!p || !s.model || !s.level || !s.positions) return null;
    var L = levelOf(p, s); if (!L) return null;
    var M = modelOf(p, s); if (!M) return null;

    var pos = chosen(p, s);
    if (!pos.length) return null;
    var f = facts(p, s);

    /* A level gated by `requiresFacts` must be refused HERE, not only in
       `levelsFor`. `levelsFor` decides what the UI OFFERS; `resolve` is what
       prices a build, and `levelTotal` calls it with a probe level id directly.
       Gating only the offer left the CVO Road Glide RR resolving a complete,
       priced level-8 build that the interface would never show - a bike we have
       decided we cannot honestly sell that upgrade to. Returning null is the
       same answer `resolve` already gives for a selection that does not exist. */
    if (L.requiresFacts && !matches(L.requiresFacts, f, s)) return null;

    var wiring = planWiring(p, L, pos, M);

    var lines = [];
    /* A part arriving from two rules is the same part, so it is listed once.
       A speaker arriving from two positions is two pairs — fairing and Tour-Pak
       on a four-speaker build resolve to the same SKU, and collapsing them would
       ship one pair and charge for one pair. Those stack. */
    /* `pos` records which speaker positions a line serves. A rider asking why a
       part is in their kit is asking a question only the resolver can answer,
       and for speakers the answer is "these are your fairing speakers" — which
       the UI cannot work out from the SKU alone once one pair serves two
       positions. Recorded here rather than reconstructed there. */
    /* `ord` is the line's place in the BOM. It is assigned where the part is
       pushed, because that is the only point at which the engine knows *why*
       the part is here — a SKU on its own cannot tell you whether it is a
       fairing grille or a Tour-Pak one. The bands are set at the call sites
       below; the fitment band is simply the order the platform file declares
       its `base` array in, so the sheet's column order lives in data rather
       than in a lookup table here.

       A part pushed twice keeps the rank it was first given. That is right:
       the earliest reason it entered the build is the reason it is a line. */
    function push(group, sku, stack, label, position, ord) {
      if (!sku || !p.parts[sku]) return;
      for (var i = 0; i < lines.length; i++) {
        if (lines[i].sku === sku) {
          if (stack) lines[i].qty++;
          if (position && lines[i].pos.indexOf(position) < 0) lines[i].pos.push(position);
          return;
        }
      }
      lines.push({ g: group, sku: sku, qty: 1, label: label || null,
                   pos: position ? [position] : [],
                   ord: typeof ord === "number" ? ord : ORD.other });
    }

    var incomplete = null;

    // A configuration the amplifier cannot drive is a hard stop, not a warning.
    if (wiring.overCapacity) {
      incomplete = pos.length + " speaker positions need more than a " +
        wiring.channels + "-channel amplifier can drive.";
    } else if (wiring.unsafe) {
      incomplete = "That combination presents " + wiring.minLoad +
        "Ω to an amplifier rated to " + wiring.minRated + "Ω.";
    } else if (wiring.unmapped && wiring.unmapped.length) {
      // Authoring fault, not a rider one — but it must stop the build rather
      // than quietly ship a system with an unamplified speaker pair in it.
      incomplete = "This system has no amplifier channel assigned to " +
        wiring.unmapped.join(" or ") + ".";
    }

    /* Every amplifier the level names, in the order it names them, then the
       external processor if there is one. A second amplifier is a line in the
       BOM exactly like the first — nothing here treats it as an accessory. */
    ampsOf(L).forEach(function (a, i) {
      push("Amplifier", a, false, null, null, ORD.amp + i);
    });
    if (L.dspPart) push("Amplifier", L.dspPart, false, null, null, ORD.amp + 5);

    // Speakers, one pair per selected position, at the impedance the wiring plan
    // calls for. A package option may substitute the front family.
    var famFor = speakerFamilies(p, s, L, pos);
    var missing = [];
    pos.forEach(function (k) {
      var fam = famFor[k];
      if (!fam) return;
      /* `spk` may name a family or an exact part. A family is resolved against
         the impedance the wiring plan calls for; an exact part is taken as
         given, because a level that pins the driver has already decided. */
      var sku = p.parts[fam] ? fam : skuFor(p, fam, wiring.ohm[k]);
      if (!sku) { missing.push(fam + " @ " + wiring.ohm[k] + "Ω"); return; }
      /* Front to back, in the platform's own position order — the sheet lists
         front speakers then rear, and `positionOrder` already says which is
         which. A part that only exists because of a position (the Tour-Pak
         grille) sits directly behind that position's speakers, not away down
         in the fitment band where nothing explains it. */
      var pi = (p.positionOrder || []).indexOf(k);
      if (pi < 0) pi = (p.positionOrder || []).length;
      push("Speakers", sku, true, null, k, ORD.speaker + pi * 2);
      if (p.positionParts && p.positionParts[k]) {
        p.positionParts[k].forEach(function (x) {
          push(groupFor(p, x), x, false, null, k, ORD.speaker + pi * 2 + 1);
        });
      }
    });
    if (missing.length && !incomplete) {
      incomplete = "No " + missing.join(", ") + " speaker available for this configuration.";
    }

    // Base lines. @grille/@rack/@power are resolved from model and amplifier.
    // The array's declaration order is the BOM Matrix column order — rack,
    // grille, lids, power, then the harnessing front to back. Reorder the file
    // and the parts list and the cart both follow; nothing here needs editing.
    (p.base || []).forEach(function (b, i) {
      if (!matches(b.when, f, s)) return;
      var sku = b.sku;
      if (sku === "@grille") sku = M.grille;
      else if (sku === "@rack") sku = M.rack;
      /* Power and ground is sized for the amplifier that draws the most, which
         is the one the level lists first. A level may override outright where
         two amplifiers together need a heavier feed than either alone. */
      else if (sku === "@power") sku = L.power || p.power[primaryAmp(L)];
      /* The rear speaker output harness is a per-MODEL part, not a per-level
         one, because Harley changed the connector for 2026: `24HDRO` fits
         2024-2025, `26HDRO` fits 2026+. Both are the same job on the same
         position, so this is one base entry resolved from the model rather than
         two entries fighting over a `when` clause.

         The model year cannot be tested here — it never crosses from the
         manifest into a platform file — so the distinction is carried by the
         MODEL KEY, exactly as the trikes already do it (TRIKE_RG3 for 2023-25,
         TRIKE_RG3_26 for 2026). A model that names no `rearOut` falls back to
         the platform's own default, so nothing authored before 2026 changes.

         The default lives in the platform file, not here: this engine holds no
         product data and must not learn a SKU. */
      else if (sku === "@rearout") sku = M.rearOut || p.rearOut;
      push(groupFor(p, sku), sku, false, null, null, ORD.base + i);
    });

    // Option-driven additions.
    visibleGroups(p, s).forEach(function (g, i) {
      var val = s[g.state];
      if (g.required && !val) { incomplete = incomplete || ("Choose " + g.label.toLowerCase() + "."); return; }
      var ch = choiceById(g, val);
      if (!ch) return;
      if (ch.refer) incomplete = incomplete || ch.refer;
      (ch.add || []).forEach(function (sku) {
        push(groupFor(p, sku), sku, false, null, null, ORD.option + i);
      });
    });

    /* Open questions declared by the platform.

       Two kinds, and the difference matters. A "block" is a part we know the
       build needs and have not yet identified — the 2024+ Tour-Pak speaker
       output harness is one. Quoting around a part like that under-supplies the
       kit, which is the failure mode that has already cost us twice, so it stops
       the build rather than warning about it. A "note" is a fitment assumption
       awaiting supplier confirmation: it does not change the BOM, but it must be
       visible rather than buried in a source-of-truth file merely because the
       fairing looks the same. Scope a note to particular models with a trait. */
    var advisories = [];
    (p.tbc || []).forEach(function (t) {
      if (!matches(t.when, f, s)) return;
      advisories.push({ id: t.id, level: t.level || "note", text: t.text });
      if (t.level === "block") incomplete = incomplete || t.text;
    });

    /* MAA harness substitution. Which loom is correct depends on more than the
       head unit — a WHD14+ running the UNLOC kit needs the version carrying the
       Maestro RR2 leg. The rules are declared per platform, first match wins, so
       the engine never has to know what a Maestro is. */
    // One SKU serves two head units; name the one the rider actually picked.
    if (f.harnessSku) push("Installation", f.harnessSku, false, f.harnessLabel, null, ORD.harness);

    /* Sort into BOM order. Explicit index tiebreak rather than trusting the
       sort to be stable: two parts in the same band (two `base` entries that
       both expand to the same rank cannot happen, but a platform file could
       always grow one) must not swap places between browsers. */
    lines.forEach(function (l, i) { l._i = i; });
    lines.sort(function (a, b) { return (a.ord - b.ord) || (a._i - b._i); });
    lines.forEach(function (l) { delete l._i; });

    /* Stock is reported, never enforced.

       `q` is baked into this file when the parts block is generated and only
       refreshes on a publish cycle, so it is KNOWN STALE — every other field
       here is stable, but inventory moves daily. Stale data may soften what we
       say; it must never gate a sale. Shopify enforces at checkout, which is
       the only place with a current number.

       Two corrections, both live faults:

       `<= 0`, not `=== 0`. An oversold line carries a NEGATIVE count and was
       sailing through as in stock — HDHU.V2 sat at -6 and read as available.

       `policy` decides the wording. CONTINUE really can be backordered.
       DENY cannot be sold at zero at all, so calling it "available to
       pre-order" promises something checkout will refuse. Policy is stable
       data, so unlike `q` it can be trusted. */
    var total = 0, bakedTotal = 0, preorder = [], confirm = [], liveCount = 0, pricedBaked = [];
    var stockLive = 0, stockedBaked = [];
    lines.forEach(function (l) {
      var pt = p.parts[l.sku];
      if (livePriceFor(pt) !== null) liveCount++; else pricedBaked.push(l.sku);
      total += priceOf(pt) * l.qty;
      bakedTotal += ((pt && typeof pt.p === "number") ? pt.p : 0) * l.qty;
      if (liveStockFor(pt) !== null) stockLive++; else stockedBaked.push(l.sku);
      var st = stockStateOf(pt);
      if (st === "confirm") confirm.push(l); else if (st === "preorder") preorder.push(l);
    });

    /* UNITS GUARD. The live map is expected in CENTS, because that is what
       Liquid's `variant.price` renders. If the section ever emits DOLLARS
       instead, every price divides by 100 a second time and a $3,720 system
       quotes $37.20 — a catastrophic error that no type check catches, because
       3720 and 372000 are both perfectly good integers.

       Nothing else can produce an order-of-magnitude collapse across a whole
       build: a real price cut is per-part and bounded. So a live total under a
       tenth of the baked total is a units fault, not a sale. Refuse the map,
       quote the baked figure, and say why. Better to be stale than to be
       wrong by a factor of a hundred. */
    var unitsSuspect = liveCount > 0 && bakedTotal > 0 && total < bakedTotal / 10;
    if (unitsSuspect) {
      total = bakedTotal;
      pricedBaked = lines.map(function (l) { return l.sku; });
      liveCount = 0;
    }

    return {
      platform: p.id, level: L, model: M, modelKey: s.model,
      positions: pos, speakers: f.speakers, dsp: f.dsp, arch: p.arch,
      wiring: wiring,
      lines: lines, total: total, preorder: preorder, confirm: confirm,
      /* Where every line's price came from. "live" is the healthy state.
         "baked" means the price map never arrived at all; "mixed" means it
         arrived but is missing parts, which is the more dangerous of the two
         because it looks like it is working. `pricedBaked` names them. */
      priceSource: unitsSuspect ? "units-suspect"
                 : (pricedBaked.length ? (liveCount ? "mixed" : "baked") : "live"),
      pricedBaked: pricedBaked,
      /* Same three states as priceSource, for stock and policy. "mixed" is
         again the one to watch: it looks like it is working. */
      stockSource: stockedBaked.length ? (stockLive ? "mixed" : "baked") : "live",
      stockedBaked: stockedBaked,
      /* Platform-wide, not build-wide. A build touching ten parts cannot see a
         map that is missing thirteen others. */
      coverage: coverage(p),
      oos: preorder.concat(confirm), incomplete: incomplete,
      advisories: advisories,
      targets: targets(p, L, famFor, pos, wiring, M),
      configVersion: p.config_version
    };
  }

  /* Which speaker family sits in each position.

     The package option is applied to the level's map FIRST, and only then are
     unspecified positions filled from the front. The other order left the
     Tour-Pak on the level's base speaker while the fairing upgraded — a CXX
     package that upgraded one pair and not the other. */
  function speakerFamilies(p, s, L, pos) {
    var spk = {}, k;
    for (k in (L.spk || {})) spk[k] = L.spk[k];

    if (L.pkg) {
      var g = groupById(p, "pkg");
      if (g) {
        var ch = choiceById(g, s[g.state]);
        if (ch && ch.spk) for (k in ch.spk) spk[k] = ch.spk[k];
      }
    }

    /* The bike has the last word. A CVO saddlebag lid is a 5×7 opening and no
       level or package changes that, so a model may pin a position's family
       outright. Applied after the package for exactly that reason — physics
       outranks the upsell. */
    var M = modelOf(p, s);
    if (M && M.spk) for (k in M.spk) spk[k] = M.spk[k];

    // Tour-Pak mirrors the front stage unless a level names it explicitly.
    var map = {};
    pos.forEach(function (x) { map[x] = spk[x] || spk[FRONT]; });
    return map;
  }

  /* What size speaker a position will actually receive, before a level is
     chosen. A model that pins the family wins; otherwise every level agrees on
     size for a given position, so the first one answers it. Derived rather than
     authored, because the copy said "two 6x9 in the bag lids" on a CVO that
     receives 5x7 — the description and the BOM were two separate truths. */
  function sizeFor(p, s, pos) {
    var M = modelOf(p, s);
    var fam = (M && M.spk && M.spk[pos]) ||
              (p.levels[0].spk && (p.levels[0].spk[pos] || p.levels[0].spk[FRONT]));
    if (!fam) return null;
    var sku = skuFor(p, fam, 2) || skuFor(p, fam, 4);
    return (sku && p.parts[sku] && p.parts[sku].size) || null;
  }

  function groupFor(p, sku) {
    var pt = p.parts[sku];
    if (!pt) return "Other";
    if (pt.wpc) return "Amplifier";
    if (pt.rms) return "Speakers";
    return "Installation";
  }

  /* Protection targets, per selected position. Target W = min(amp output per
     channel, speaker RMS). Target Vrms = sqrt(target W x presented load).
     Never authored — always derived. */
  function targets(p, L, famFor, pos, wiring, M) {
    var prim = ampRecord(p, L, M);
    var wpc = prim.wpc || null;
    var out = { wpc: wpc, byPosition: {} };
    pos.forEach(function (k) {
      /* A level may name the exact part for a position rather than a family.
         Families exist so one level can serve every impedance the wiring plan
         might call for; a level that pins the driver has already made that
         choice, and re-deriving it would throw the choice away. */
      var sku = (L.spk && p.parts[L.spk[k]]) ? L.spk[k] : skuFor(p, famFor[k], wiring.ohm[k]);
      var pt = sku && p.parts[sku];
      // Which amplifier drives this position, and what it makes into that load.
      var ampPt = p.parts[(wiring.ampOf && wiring.ampOf[k]) || primaryAmp(L)] || prim;
      var chW = wpcFor(ampPt, wiring.presentedLoad[k]);
      if (!pt || !chW || !pt.rms) { out.byPosition[k] = null; return; }
      var w = Math.min(chW, pt.rms);
      out.byPosition[k] = {
        sku: sku, w: w, v: Math.sqrt(w * wiring.presentedLoad[k]),
        capped: pt.rms < chW, rms: pt.rms, ohm: wiring.ohm[k],
        /* Name the amplifier the figures were computed against. On a
           speaker-only build there is no Cicada part, so this reports the
           factory amplifier's part number instead of null — otherwise the
           output publishes 125 W at 2 ohms with nothing saying which
           amplifier that is, and the Road Glide RR (76001485) would be
           indistinguishable from the other four (76001294). */
        amp: (wiring.ampOf && wiring.ampOf[k]) || primaryAmp(L) ||
             (M && M.oemAmp && M.oemAmp.pn) || null, chW: chW
      };
    });
    out.front = out.byPosition[FRONT] || null;
    return out;
  }

  function levelTotal(p, s, levelId) {
    var probe = {}; for (var k in s) probe[k] = s[k];
    probe.level = levelId;
    var b = resolve(p, probe);
    return b ? b.total : null;
  }

  /* Difference between two resolutions, by SKU *and* quantity. Comparing bare
     SKU lists hid a real change: adding a Tour-Pak to a build whose front speaker
     is the same part goes from one pair to two, which is a $940 difference that
     showed as "nothing added". */
  function diff(cur, prv) {
    if (!cur || !prv) return null;
    function index(r) {
      var ix = {};
      r.lines.forEach(function (l) { ix[l.sku] = l.qty; });
      return ix;
    }
    var A = index(cur), B = index(prv), added = [], removed = [], changed = [], kept = 0, k;
    for (k in A) {
      if (!(k in B)) added.push({ sku: k, qty: A[k] });
      else if (A[k] !== B[k]) changed.push({ sku: k, from: B[k], to: A[k] });
      else kept++;
    }
    for (k in B) if (!(k in A)) removed.push({ sku: k, qty: B[k] });
    return { added: added, removed: removed, changed: changed,
             delta: cur.total - prv.total, kept: kept };
  }

  /* Which levels this bike can actually be offered.

     Until now the ladder was platform-wide: every 2024+ model saw Levels 1-5,
     which was correct because every one of them has a fairing and the rest of
     the ladder scales by amplifier, not by bodywork. That stops being true the
     moment a level depends on a position only some models have — a build that
     puts drivers in the lower fairing cannot be offered to a bike without
     lowers, and offering it and then failing to resolve would be the worst of
     both worlds.

     `requires` lists positions the MODEL must physically have. Not positions
     the rider has selected — the level has to appear before they have chosen
     anything, or they can never get to it. Same rule the position list itself
     follows: the bike decides what is possible, the rider decides what to take.

     A level with no `requires` is offered to every model, so nothing that
     exists today changes. */
  /* `maxPositions` is the opposite test to `requires`: not what the bike HAS,
     but how many positions this level can actually drive.

     The derived plan gives the front its own channel pair and shares what is
     left, so presented load is speaker impedance divided by however many pairs
     share. On a four-channel amplifier three positions land on 4Ω speakers,
     which exist. A fourth needs 6Ω, which Cicada does not make — so the level
     resolves `incomplete` and the rider is shown a priced build missing three
     of its four speaker pairs. Offering a level that cannot resolve is exactly
     what the `requires` comment above calls the worst of both worlds.

     Levels with an authored `channels` map are exempt by omission: they state
     their own wiring, which is the whole reason the eight-speaker builds work.

     This one DOES read the rider's selection, unlike `requires`. That is safe
     because `chosen()` returns the front alone before anything is picked, so
     `want` is 1 and nothing is hidden early. */
  function levelsFor(p, s) {
    var have = positionsFor(p, s);
    var want = chosen(p, s).length;
    /* Computed once, outside the filter. `requiresFacts` may only test facts
       that describe the BIKE, never the build: `facts()` is evaluated against
       whichever level is currently selected, so a level-derived fact would make
       the list depend on the answer it is being used to choose. */
    var f = facts(p, s);
    return (p.levels || []).filter(function (L) {
      if (L.maxPositions && want > L.maxPositions) return false;
      if (L.requiresFacts && !matches(L.requiresFacts, f, s)) return false;
      if (!L.requires || !L.requires.length) return true;
      return L.requires.every(function (k) { return have.indexOf(k) >= 0; });
    });
  }

  var API = { loadPlatform: loadPlatform, register: register, platform: platform,
              newState: newState, positionsFor: positionsFor, visibleGroups: visibleGroups,
              resolve: resolve, levelTotal: levelTotal, diff: diff, facts: facts,
              planWiring: planWiring, skuFor: skuFor, sizeFor: sizeFor,
              levelsFor: levelsFor, ampsOf: ampsOf, hasDsp: hasDsp,
              setLivePrices: setLivePrices, priceOf: priceOf,
              setLiveStock: setLiveStock, stockStateOf: stockStateOf,
              coverage: coverage, FRONT: FRONT };
  if (typeof module === "object" && module.exports) module.exports = API;
  root.MAAKB = API;
})(typeof self !== "undefined" ? self : this);
